import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  Package,
  Calendar,
  Clock,
  MapPin,
  Download,
  XCircle,
  CheckCircle2,
  Repeat,
  RefreshCw,
  Info,
  Wallet,
  CreditCard,
  Gift,
  Tag,
  Receipt,
  GiftIcon,
  Headphones,
  Star,
  Send,
  ImagePlus,
  X,
  PlusCircle,
  RotateCcw,
} from "lucide-react";

import {
  useLazyGetCustomerOrderDetailsQuery,
  useRetryRevisionMutation,
  useReviewRevisionMutation,
  useRateOrderItemsMutation,
} from "../../../redux/actions/Customer/cartCustomer";

import { useAlert } from "../../../components/customer/AlertContext";

import {
  IRetryRevisionRequest,
  ISubmitReviewItem,
  ISubmitReviewRequest,
} from "../../../types/Request/CustomerRequest";

import { ImagePath } from "../../../assests";
import { useNavigate } from "react-router-dom";
import { useUploadCustomerFileMutation } from "../../../redux/actions/Customer/profileCustomer";

interface CustomerOrderDetailsProps {
  order: { orderId: number };
  onBack: () => void;
}

interface ItemReviewState {
  rating: number;
  comment: string;
  files: File[];
  previews: string[];
}

const formatCountdown = (seconds: number) => {
  if (seconds <= 0) return "Expired";

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  return `${h > 0 ? `${h}h ` : ""}${m}m ${s}s`;
};

const toneClasses: Record<string, string> = {
  replace: "bg-blue-50 text-blue-700 border-blue-200",
  reject: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
};

const CustomerOrderDetails: React.FC<CustomerOrderDetailsProps> = ({
  order,
  onBack,
}) => {
  const { showConfirm } = useAlert();
  const navigate = useNavigate();

  const [nowTick, setNowTick] = useState(Date.now());

  const [decisions, setDecisions] = useState<
    Record<number, "Accept" | "Reject" | undefined>
  >({});

  // Bulk Item Review State
  const [bulkReviews, setBulkReviews] = useState<
    Record<number, ItemReviewState>
  >({});

  const [triggerOrderDetails, { data, isFetching }] =
    useLazyGetCustomerOrderDetailsQuery();

  const [reviewRevision, { isLoading: isSubmitting }] =
    useReviewRevisionMutation();

  const [createPaymentSession] = useRetryRevisionMutation();

  const [rateOrderItems, { isLoading: isSubmittingReviews }] =
    useRateOrderItemsMutation();

  const [uploadCustomerFile] = useUploadCustomerFileMutation();

  useEffect(() => {
    if (order?.orderId) {
      triggerOrderDetails(order.orderId);
    }
  }, [order?.orderId]);

  const revision = data?.currentRevision;
  const reviewData = data?.review;

  const hasPendingRevision =
    !!revision &&
    revision.status === "PendingCustomerApproval" &&
    !revision.isExpired;

  useEffect(() => {
    if (revision?.revisionId) {
      setDecisions({});
    }
  }, [revision?.revisionId]);

  useEffect(() => {
    if (!hasPendingRevision) return;

    const interval = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [hasPendingRevision]);

  const remainingSeconds = revision?.expiresOn
    ? Math.max(
      0,
      Math.floor(
        (new Date(revision.expiresOn).getTime() - nowTick) / 1000,
      ),
    )
    : 0;

  const totalChanges = revision?.changes?.length || 0;

  const isPaymentPending =
    revision?.status === "PendingSettlement" &&
    revision?.paymentStatus === "PaymentPending";

  const isRefundPending =
    revision?.status === "PendingSettlement" &&
    revision?.paymentStatus === "RefundPending";

  const isDelivered =
    data?.header?.orderStatus?.toLowerCase() === "delivered" ||
    (order as any)?.displayStatus?.toLowerCase() === "delivered" ||
    (order as any)?.orderStatus === 6;

  // Rating & text handlers
  const handleRatingChange = (orderLineId: number, rating: number) => {
    setBulkReviews((prev) => ({
      ...prev,
      [orderLineId]: {
        rating,
        comment: prev[orderLineId]?.comment || "",
        files: prev[orderLineId]?.files || [],
        previews: prev[orderLineId]?.previews || [],
      },
    }));
  };

  const handleCommentChange = (
    orderLineId: number,
    comment: string,
  ) => {
    setBulkReviews((prev) => ({
      ...prev,
      [orderLineId]: {
        rating: prev[orderLineId]?.rating || 5,
        comment,
        files: prev[orderLineId]?.files || [],
        previews: prev[orderLineId]?.previews || [],
      },
    }));
  };

  // Image Upload handlers
  const handleAddImages = (
    orderLineId: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!e.target.files?.length) return;

    const selectedFiles = Array.from(e.target.files);

    const newPreviews = selectedFiles.map((file) =>
      URL.createObjectURL(file),
    );

    setBulkReviews((prev) => {
      const existing = prev[orderLineId] || {
        rating: 5,
        comment: "",
        files: [],
        previews: [],
      };

      return {
        ...prev,
        [orderLineId]: {
          ...existing,
          files: [...existing.files, ...selectedFiles],
          previews: [...existing.previews, ...newPreviews],
        },
      };
    });
  };

  const handleRemoveImage = (
    orderLineId: number,
    index: number,
  ) => {
    setBulkReviews((prev) => {
      const existing = prev[orderLineId];

      if (!existing) return prev;

      return {
        ...prev,
        [orderLineId]: {
          ...existing,
          files: existing.files.filter((_, i) => i !== index),
          previews: existing.previews.filter((_, i) => i !== index),
        },
      };
    });
  };

  // Submit All Reviews
  const handleSubmitAllReviews = async () => {
    const unreviewedEntries = Object.entries(bulkReviews).filter(
      ([, val]) => val.rating > 0,
    );

    if (unreviewedEntries.length === 0) {
      showConfirm({
        title: "No Ratings Selected",
        message:
          "Please select star ratings for the items you wish to review.",
        type: "warning",
        confirmText: "OK",
      });

      return;
    }

    try {
      const payloadItems = [];

      for (const [orderLineId, reviewState] of unreviewedEntries) {
        const uploadedUrls: string[] = [];

        for (const file of reviewState.files) {
          const response = await uploadCustomerFile({
            File: file,
            FolderName: "Reviews/Items",
          }).unwrap();

          if (response?.fileUrl) {
            uploadedUrls.push(response.fileUrl);
          }
        }

        payloadItems.push({
          orderItemId: Number(orderLineId),
          rating: reviewState.rating,
          comment: reviewState.comment || "",
          images: uploadedUrls,
        });
      }

      await rateOrderItems({
        OrderId: order.orderId,
        data: {
          items: payloadItems as any,
        },
      }).unwrap();

      showConfirm({
        title: "Reviews Submitted",
        message:
          "Thank you! All product reviews and images have been saved.",
        type: "success",
        confirmText: "OK",
      });

      setBulkReviews({});
      triggerOrderDetails(order.orderId);
    } catch (err: any) {
      showConfirm({
        title: "Submission Failed",
        message:
          err?.data?.Message ||
          err?.message ||
          "Could not submit reviews. Please try again.",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  const buildReviewPayload = (
    updatedDecisions: Record<
      number,
      "Accept" | "Reject" | undefined
    >,
  ): ISubmitReviewRequest => {
    const items: ISubmitReviewItem[] = revision!.changes.map(
      (change: any, idx: number) => {
        const targetRevisionItemId =
          change.revisionItemId ||
          change.originalItem?.revisionItemId ||
          change.replacementItems?.[0]?.revisionItemId;

        return {
          revisionItemId: targetRevisionItemId,
          accepted: updatedDecisions[idx] === "Accept",
        };
      },
    );

    return {
      reviewId: reviewData?.reviewId,
      customerRemarks: "Customer responded to revision.",
      items,
    };
  };

  const handleContactSupport = () => {
    if (!data?.header) return;

    navigate("/contactus", {
      state: {
        orderId: order.orderId,
        orderNumber: data.header.orderNumber,
      },
    });
  };

  const handleDecision = async (
    changeIdx: number,
    value: "Accept" | "Reject",
  ) => {
    if (!revision) return;

    const nextDecisions = {
      ...decisions,
      [changeIdx]: value,
    };

    setDecisions(nextDecisions);

    const decidedEntries = Object.entries(nextDecisions).filter(
      ([, v]) => v !== undefined,
    );

    if (
      totalChanges > 0 &&
      decidedEntries.length === totalChanges
    ) {
      try {
        await reviewRevision(
          buildReviewPayload(nextDecisions),
        ).unwrap();

        triggerOrderDetails(order.orderId);
      } catch (err: any) {
        showConfirm({
          title: "Submission Failed",
          message:
            err?.data?.Message ||
            err?.message ||
            "Could not submit your response. Please try again.",
          type: "error",
          confirmText: "OK",
        });
      }
    }
  };

  const handleRetrypayment = async (id: number) => {
    try {
      const req: IRetryRevisionRequest = {
        revisionId: id,
      };

      const response = await createPaymentSession(req).unwrap();

      if (response.success && response.data.success) {
        window.location.href = response.data.paymentUrl;
      }
    } catch (err: any) {
      showConfirm({
        title: "Payment Initialization Failed",
        message:
          err?.data?.Message ||
          "Could not start payment session.",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  const handleDownloadInvoice = () => {
    if (!data) return;

    const invoiceWindow = window.open("", "_blank");

    if (!invoiceWindow) {
      alert("Please allow popups to download the invoice.");
      return;
    }

    const {
      header,
      customer,
      address,
      pricing,
      items = [],
    } = data;

    const orderNumber = header?.orderNumber ?? "N/A";
    const orderDate = header?.orderDate;
    const currency = header?.currency ?? "AED";

    const formattedOrderDate = orderDate
      ? new Date(orderDate).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
      : "N/A";

    const displayStatus = header?.orderStatus ?? "Confirmed";

    const activeInvoiceItems = items.filter(
      (item: any) => item.quantity > 0,
    );

const itemsHtml = activeInvoiceItems
  .map(
    (item: any) => `
      <tr>
        <td class="item-column">
          <div class="item-wrapper">
            ${
              item.imageUrl
                ? `<img 
                    src="${item.imageUrl}" 
                    class="item-thumb" 
                    alt="${item.name}" 
                  />`
                : `<div class="item-thumb-placeholder">
                    No Img
                  </div>`
            }

            <div>
              <div class="item-name">
                ${item.name ?? "N/A"}
              </div>

              ${
                item.subtitle
                  ? `
                    <div class="item-subtitle">
                      ${item.subtitle}
                    </div>
                  `
                  : ""
              }

              ${
                item.itemType?.toLowerCase() === "pet"
                  ? `
                    <div
                      class="item-pet-details"
                      style="
                        margin-top: 5px;
                        font-size: 9px;
                        line-height: 1.6;
                      "
                    >

                      ${
                        item.purchaseType
                          ? `
                            <div
                              style="
                                color: #7c3aed;
                                font-weight: 700;
                              "
                            >
                              ${
                                item.purchaseType === "PartialBooking"
                                  ? "Partial Booking"
                                  : item.purchaseType === "FullPayment"
                                    ? "Full Payment"
                                    : item.purchaseType
                              }
                            </div>
                          `
                          : ""
                      }

                      ${
                        item.unitPrice !== undefined &&
                        item.unitPrice !== null
                          ? `
                            <div
                              style="
                                color: #334155;
                                font-weight: 600;
                              "
                            >
                              Total Amount:
                              ${currency}
                              ${Number(
                                item.unitPrice
                              ).toFixed(2)}
                            </div>
                          `
                          : item.unitSalePrice !== undefined &&
                            item.unitSalePrice !== null
                            ? `
                              <div
                                style="
                                  color: #334155;
                                  font-weight: 600;
                                "
                              >
                                Total Amount:
                                ${currency}
                                ${Number(
                                  item.unitSalePrice
                                ).toFixed(2)}
                              </div>
                            `
                            : ""
                      }

                      ${
                        item.purchaseAmount !== undefined &&
                        item.purchaseAmount !== null
                          ? `
                            <div
                              style="
                                color: #475569;
                                font-weight: 600;
                              "
                            >
                              Paid / Booking Amount:
                              ${currency}
                              ${Number(
                                item.purchaseAmount
                              ).toFixed(2)}
                            </div>
                          `
                          : ""
                      }

                      ${
                        item.remainingAmount !== undefined &&
                        item.remainingAmount !== null &&
                        Number(item.remainingAmount) > 0
                          ? `
                            <div
                              style="
                                color: #b45309;
                                font-weight: 700;
                              "
                            >
                              Remaining Amount:
                              ${currency}
                              ${Number(
                                item.remainingAmount
                              ).toFixed(2)}
                            </div>
                          `
                          : ""
                      }

                    </div>
                  `
                  : ""
              }

              ${
                item.isBundleItem &&
                Number(item.savingAmount ?? 0) > 0
                  ? `
                    <div
                      style="
                        margin-top: 3px;
                        font-size: 9px;
                        color: #15803d;
                        font-weight: 600;
                      "
                    >
                      You save ${currency}
                      ${Number(
                        item.savingAmount
                      ).toFixed(2)}
                    </div>
                  `
                  : ""
              }
            </div>
          </div>
        </td>

        <td class="type-column">
          <span class="badge badge-type">
            ${item.itemType ?? "Product"}
          </span>
        </td>

        <td class="quantity-column text-center">
          ${item.quantity ?? 0}
        </td>

        <td class="amount-column text-right">
          ${
            item.isFreeItem
              ? `<span class="free-item">FREE</span>`
              : item.itemType?.toLowerCase() === "pet"
                ? `
                  <div
                    style="
                      display: flex;
                      flex-direction: column;
                      align-items: flex-end;
                      gap: 3px;
                    "
                  >

                    <div
                      style="
                        font-size: 11px;
                        font-weight: 700;
                        color: #1e293b;
                      "
                    >
                      ${currency}
                      ${Number(
                        item.purchaseAmount ??
                        item.totalPrice ??
                        item.unitSalePrice ??
                        item.unitPrice ??
                        0
                      ).toFixed(2)}
                    </div>

                    ${
                      item.remainingAmount !== undefined &&
                      item.remainingAmount !== null &&
                      Number(item.remainingAmount) > 0
                        ? `
                          <div
                            style="
                              font-size: 9px;
                              font-weight: 700;
                              color: #b45309;
                            "
                          >
                            Remaining:
                            ${currency}
                            ${Number(
                              item.remainingAmount
                            ).toFixed(2)}
                          </div>
                        `
                        : ""
                    }

                  </div>
                `
                : `
                  ${currency}
                  ${Number(
                    item.totalPrice ??
                    item.unitSalePrice * item.quantity
                  ).toFixed(2)}
                `
          }
        </td>
      </tr>
    `,
  )
  .join("");

    const watermarkImage =
      ImagePath.Petland_Watermark?.default ??
      ImagePath.Petland_Watermark;

    const petlandLogo =
      ImagePath.Petland_Watermark2?.default ??
      ImagePath.Petland_Watermark2;

    const invoiceHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Invoice - ${orderNumber}</title>

        <meta charset="UTF-8" />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        />

        <style>
          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, Helvetica, sans-serif;
            color: #1e293b;
            background: #ffffff;
            font-size: 12px;
          }

          .page {
            width: 100%;
            max-width: 794px;
            margin: 0 auto;
            padding: 30px 34px;
            background: #ffffff;
            position: relative;
          }

          .invoice-container {
            width: 100%;
            position: relative;
            z-index: 1;
          }

          .invoice-watermark {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 360px;
            max-width: 50%;
            height: auto;
            opacity: 0.08;
            z-index: 0;
            pointer-events: none;
            object-fit: contain;
          }

          .invoice-header {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 30px;
            padding-bottom: 16px;
            border-bottom: 2px solid #6DBE45;
            position: relative;
            z-index: 2;
            min-height: 90px;
          }

          .invoice-logo-wrapper {
            flex-shrink: 0;
          }

          .invoice-logo {
            display: block;
            width: 160px;
            height: auto;
            object-fit: contain;
          }

          .brand-subtitle {
            margin-top: 3px;
            font-size: 10px;
            color: #64748b;
          }

          .invoice-title {
            flex: 1;
            text-align: right;
          }

          .invoice-title h1 {
            margin: 0;
            font-size: 22px;
            font-weight: 700;
            color: #0f172a;
          }

          .invoice-title p {
            margin: 3px 0 0;
            font-size: 11px;
            color: #64748b;
          }

          .invoice-info {
            width: 100%;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-top: 20px;
            position: relative;
            z-index: 2;
          }

          .info-card {
            padding: 12px;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
          }

          .section-title {
            margin-bottom: 8px;
            font-size: 9px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.8px;
            color: #64748b;
          }

          .info-row {
            margin-bottom: 4px;
            font-size: 11px;
            line-height: 1.4;
            color: #334155;
          }

          .info-label {
            font-weight: 600;
            color: #475569;
          }

          .order-summary {
            width: 100%;
            margin-top: 16px;
            padding: 10px 14px;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            position: relative;
            z-index: 2;
          }

          .order-summary-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 11px;
            color: #475569;
          }

          .order-status {
            display: inline-flex;
            align-items: center;
            padding: 3px 9px;
            border-radius: 999px;
            background: #f0fdf4;
            border: 1px solid #bbf7d0;
            color: #15803d !important;
            font-size: 10px;
            font-weight: 700 !important;
          }

          .items-table {
            width: 100%;
            margin-top: 16px;
            border-collapse: collapse;
            table-layout: fixed;
            position: relative;
            z-index: 2;
          }

          .items-table th {
            padding: 8px 10px;
            background: #f8fafc;
            text-align: left;
            font-size: 9px;
            font-weight: 700;
            text-transform: uppercase;
            color: #64748b;
            border-bottom: 1px solid #e2e8f0;
          }

          .items-table td {
            padding: 8px 10px;
            font-size: 11px;
            vertical-align: middle;
            border-bottom: 1px solid #f1f5f9;
          }

          .item-wrapper {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .item-thumb {
            width: 38px;
            height: 38px;
            object-fit: cover;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
            flex-shrink: 0;
          }

          .item-thumb-placeholder {
            width: 38px;
            height: 38px;
            background: #f1f5f9;
            color: #94a3b8;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8px;
            font-weight: 600;
            flex-shrink: 0;
          }

          .item-name {
            font-weight: 600;
            color: #1e293b;
          }

          .item-subtitle {
            margin-top: 2px;
            font-size: 9px;
            color: #94a3b8;
          }

          .item-pet-details {
            margin-top: 3px;
            font-size: 9px;
            color: #7c3aed;
            font-weight: 600;
          }

          .badge {
            display: inline-block;
            padding: 1px 5px;
            border-radius: 4px;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            margin-right: 4px;
          }

          .badge-type {
            background: #f1f5f9;
            color: #475569;
          }

          .free-item {
            font-weight: 700;
            color: #15803d;
          }

          .text-center {
            text-align: center !important;
          }

          .text-right {
            text-align: right !important;
          }

          .payment-summary {
            width: 100%;
            margin-top: 20px;
            padding: 12px 14px;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            position: relative;
            z-index: 2;
          }

          .payment-summary .section-title {
            margin-bottom: 8px;
            padding-bottom: 7px;
            border-bottom: 1px solid #e2e8f0;
          }

          .payment-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 4px 0;
            font-size: 11px;
            color: #475569;
          }

          .payment-row span:last-child {
            font-weight: 600;
            color: #1e293b;
            text-align: right;
          }

          .payment-row.total {
            margin-top: 6px;
            padding-top: 8px;
            border-top: 2px solid #e2e8f0;
            font-size: 13px;
            font-weight: 700;
            color: #2F5E2E;
          }

          .payment-row.total span {
            color: #2F5E2E;
            font-weight: 700;
          }

          .invoice-footer {
            width: 100%;
            margin-top: 26px;
            padding-top: 12px;
            border-top: 1px solid #e2e8f0;
            text-align: center;
            font-size: 10px;
            line-height: 1.5;
            color: #94a3b8;
            position: relative;
            z-index: 2;
          }

          .invoice-footer strong {
            color: #2F5E2E;
            font-size: 11px;
          }

          @page {
            size: A4;
            margin: 0;
          }

          @media print {
            html,
            body {
              width: 100%;
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff;
            }

            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .page {
              width: 100%;
              max-width: none;
              margin: 0;
              padding: 10mm;
            }

            .invoice-watermark {
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 320px;
              opacity: 0.08;
            }
          }
        </style>
      </head>

      <body>
        <div class="page">
          <div class="invoice-container">

            <img
              src="${watermarkImage}"
              class="invoice-watermark"
              alt="PetLand Watermark"
            />

            <div class="invoice-header">
              <div class="invoice-logo-wrapper">
                <img
                  src="${petlandLogo}"
                  class="invoice-logo"
                  alt="PetLand Logo"
                />

                <div class="brand-subtitle">
                  More than a Pet Store
                </div>

                <div class="brand-subtitle">
                  TNR: 100502238700003
                </div>

                <div class="brand-subtitle">
                  Mobile: 043372862
                </div>
              </div>

              <div class="invoice-title">
                <h1>INVOICE</h1>
                <p>Order #${orderNumber}</p>
                <p>${formattedOrderDate}</p>
              </div>
            </div>

            <div class="invoice-info">

              <div class="info-card">
                <div class="section-title">
                  Bill To
                </div>

                <div class="info-row">
                  <span class="info-label">Name:</span>
                  ${customer?.name ?? "N/A"}
                </div>

                <div class="info-row">
                  <span class="info-label">Email:</span>
                  ${customer?.email ?? "N/A"}
                </div>

                <div class="info-row">
                  <span class="info-label">Mobile:</span>
                  ${customer?.mobile ??
      address?.phoneNumber ??
      "N/A"
      }
                </div>
              </div>

              <div class="info-card">
                <div class="section-title">
                  Delivery Address
                </div>

                <div class="info-row">
                  <span class="info-label">Name:</span>
                  ${address?.fullName ??
      customer?.name ??
      "N/A"
      }
                </div>

                ${address?.phoneNumber
        ? `<div class="info-row">
                        <span class="info-label">Mobile:</span>
                        ${address.phoneNumber}
                      </div>`
        : ""
      }

                ${address?.addressLine1
        ? `<div class="info-row">
                        ${address.addressLine1}
                      </div>`
        : ""
      }

                ${address?.addressLine2
        ? `<div class="info-row">
                        ${address.addressLine2}
                      </div>`
        : ""
      }

                <div class="info-row">
                  ${[
        address?.city,
        address?.state,
        address?.postalCode,
      ]
        .filter(Boolean)
        .join(", ")}
                </div>

                ${address?.country
        ? `<div class="info-row">
                        ${address.country}
                      </div>`
        : ""
      }
              </div>

            </div>

            <div class="order-summary">
              <div class="order-summary-row">
                <span class="info-label">
                  Order Status
                </span>

                <strong class="order-status">
                  ${displayStatus}
                </strong>
              </div>
            </div>

            <table class="items-table">

              <colgroup>
                <col style="width: 52%;" />
                <col style="width: 16%;" />
                <col style="width: 14%;" />
                <col style="width: 18%;" />
              </colgroup>

              <thead>
                <tr>
                  <th>Item</th>
                  <th>Type</th>
                  <th class="text-center">
                    Quantity
                  </th>
                  <th class="text-right">
                    Amount
                  </th>
                </tr>
              </thead>

              <tbody>
                ${itemsHtml}
              </tbody>

            </table>

            <div class="payment-summary">

              <div class="section-title">
                Payment Summary
              </div>

              <div class="payment-row">
                <span>Items Subtotal</span>
                <span>
                  ${currency}
                  ${Number(
        pricing?.subTotal ??
        pricing?.productTotal ??
        0,
      ).toFixed(2)}
                </span>
              </div>
              ${remainingAmount > 0
  ? `<div class="payment-row">
      <span>Remaining Amount</span>
      <span style="font-weight: 600;">
        ${currency} ${remainingAmount.toFixed(2)}
      </span>
    </div>`
  : ""
}

              ${totalSaving > 0
        ? `<div class="payment-row">
                      <span>Total Savings</span>
                      <span style="color: #15803d; font-weight: 600;">
                        -${currency} ${totalSaving.toFixed(2)}
                      </span>
                    </div>`
        : ""
      }

              ${(pricing?.couponDiscount ??
        pricing?.discount ??
        0) > 0
        ? `<div class="payment-row">
                      <span>
                        Discount ${pricing?.couponCode
          ? `(${pricing.couponCode})`
          : ""
        }
                      </span>

                      <span style="color: #dc2626;">
                        -${currency}
                        ${Number(
          pricing?.couponDiscount ??
          pricing?.discount,
        ).toFixed(2)}
                      </span>
                    </div>`
        : ""
      }

              ${(pricing?.rewardPointDiscount ?? 0) > 0
        ? `<div class="payment-row">
                      <span>
                        Reward Points Disc.
                      </span>

                      <span style="color: #dc2626;">
                        -${currency}
                        ${Number(
          pricing.rewardPointDiscount,
        ).toFixed(2)}
                      </span>
                    </div>`
        : ""
      }

              ${(pricing?.walletAmountUsed ?? 0) > 0
        ? `<div class="payment-row">
                      <span>Wallet Paid</span>

                      <span style="color: #dc2626;">
                        -${currency}
                        ${Number(
          pricing.walletAmountUsed,
        ).toFixed(2)}
                      </span>
                    </div>`
        : ""
      }

              ${(pricing?.vatAmount ??
        pricing?.tax ??
        0) > 0
        ? `<div class="payment-row">
                      <span>VAT (Tax)</span>

                      <span>
                        ${currency}
                        ${Number(
          pricing?.vatAmount ??
          pricing?.tax,
        ).toFixed(2)}
                      </span>
                    </div>`
        : ""
      }

              <div class="payment-row">
                <span>Delivery Fee</span>

                <span>
                  ${(pricing?.deliveryFee ??
        pricing?.shipping ??
        0) > 0
        ? `${currency} ${Number(
          pricing?.deliveryFee ??
          pricing?.shipping,
        ).toFixed(2)}`
        : "Free"
      }
                </span>
              </div>

              ${(pricing?.platformFee ?? 0) > 0
        ? `<div class="payment-row">
                      <span>Platform Fee</span>

                      <span>
                        ${currency}
                        ${Number(
          pricing.platformFee,
        ).toFixed(2)}
                      </span>
                    </div>`
        : ""
      }

              ${(pricing?.smallOrderFee ?? 0) > 0
        ? `<div class="payment-row">
                      <span>Small Order Fee</span>

                      <span>
                        ${currency}
                        ${Number(
          pricing.smallOrderFee,
        ).toFixed(2)}
                      </span>
                    </div>`
        : ""
      }

              <div class="payment-row total">
                <span>Total Amount</span>

                <span>
                  ${currency}
                  ${Number(
        pricing?.totalAmount ??
        pricing?.total ??
        0,
      ).toFixed(2)}
                </span>
              </div>

              ${(pricing?.additionalPaymentAmount ??
        0) > 0
        ? `<div
                      class="payment-row"
                      style="
                        margin-top: 4px;
                        padding-top: 4px;
                        border-top: 1px dashed #e2e8f0;
                        color: #dc2626;
                        font-weight: 600;
                      "
                    >
                      <span>
                        Additional Payment
                      </span>

                      <span style="color: #dc2626;">
                        +${currency}
                        ${Number(
          pricing.additionalPaymentAmount,
        ).toFixed(2)}
                      </span>
                    </div>`
        : ""
      }

              ${(pricing?.refundedAmount ?? 0) > 0
        ? `<div
                      class="payment-row"
                      style="
                        margin-top: 4px;
                        padding-top: 4px;
                        border-top: 1px dashed #e2e8f0;
                        color: #15803d;
                        font-weight: 600;
                      "
                    >
                      <span>
                        Refund Amount
                      </span>

                      <span style="color: #15803d;">
                        -${currency}
                        ${Number(
          pricing.refundedAmount,
        ).toFixed(2)}
                      </span>
                    </div>`
        : ""
      }

              ${(pricing?.gatewayAmount ?? 0) > 0
        ? `<div
                      class="payment-row"
                      style="
                        margin-top: 4px;
                        font-weight: bold;
                        color: #1d4ed8;
                      "
                    >
                      <span>
                        Revised Total
                      </span>

                      <span>
                        ${currency}
                        ${Number(
          pricing.gatewayAmount,
        ).toFixed(2)}
                      </span>
                    </div>`
        : ""
      }

            </div>

            <div class="invoice-footer">
              <strong>
                Thank you for shopping with PetLand!
              </strong>

              <br />

              This invoice is system generated.

              <br />

             Note: All pets are non-refundable. No returns or exchanges will be accepted on pets or products. Deposits and booking amounts are also non-refundable.
            </div>

          </div>
        </div>
      </body>
    </html>
    `;

    invoiceWindow.document.open();
    invoiceWindow.document.write(invoiceHtml);
    invoiceWindow.document.close();
  };

  if (isFetching && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <RefreshCw
          className="animate-spin text-[#6DBE45]"
          size={32}
        />

        <p className="text-xs font-bold text-slate-500">
          Loading Order Details...
        </p>
      </div>
    );
  }

  if (!data) return null;

  const p = data?.pricing;

  const currency = data?.header?.currency ?? "AED";

  const couponDisc =
    p?.couponDiscount ?? p?.discount ?? 0;

  const rewardDisc =
    p?.rewardPointDiscount ?? 0;

  const walletUsed =
    p?.walletAmountUsed ?? 0;

  const deliveryFee =
    p?.deliveryFee ?? p?.shipping ?? 0;

  const vatAmount =
    p?.vatAmount ?? p?.tax ?? 0;

  const platformFee =
    p?.platformFee ?? 0;

  const smallOrderFee =
    p?.smallOrderFee ?? 0;

  const totalAmount =
    p?.totalAmount ?? p?.total ?? 0;
  const remainingAmount = Number(p?.remainingAmount ?? 0);

  const gatewayAmount =
    p?.gatewayAmount ?? 0;

  const rewardPointsEarned =
    p?.rewardPointsEarned ?? 0;

  const rewardPointsUsed =
    p?.rewardPointsUsed ?? 0;

  const additionalPaymentAmount =
    p?.additionalPaymentAmount ?? 0;

  const refundedAmount =
    p?.refundedAmount ?? 0;

  const hasUnreviewedDeliveredItems =
    isDelivered &&
    (data.items || []).some(
      (item: any) =>
        item.quantity > 0 &&
        (item.rating === null ||
          item.rating === undefined ||
          !item.reviewed),
    );

  const totalSaving = p?.savedAmount || 0

  return (
    <div className="w-full p-4 sm:p-6 bg-white min-h-screen rounded-2xl space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3 border-b border-slate-200 pb-4">

        <button
          onClick={onBack}
          className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft size={16} />
        </button>

        <div className="flex-1 min-w-0">

          <h2 className="text-lg font-bold text-[#2F5E2E] flex items-center gap-2">
            <Package
              size={18}
              className="text-[#6DBE45]"
            />

            {data.header.orderNumber}
          </h2>

          <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">

            <span className="flex items-center gap-1">
              <Calendar size={12} />

              {new Date(
                data.header.orderDate,
              ).toLocaleDateString()}
            </span>

            <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
              {data.header.orderStatus}
            </span>

            <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
              {data.header.paymentStatus}
            </span>

          </div>
        </div>

        {/* Header Actions */}
        <div className="flex items-center gap-2">

          <button
            type="button"
            onClick={handleContactSupport}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold hover:bg-emerald-100 transition-colors"
          >
            <Headphones size={15} />
            Contact Support
          </button>

          {data.actions?.canDownloadInvoice && (
            <button
              onClick={handleDownloadInvoice}
              className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors"
              title="Download Invoice"
            >
              <Download
                size={16}
                style={{ color: "#5FB63F" }}
              />
            </button>
          )}

        </div>
      </div>

      {/* Pending Revision Banner */}
      {hasPendingRevision && (
        <div className="relative rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-4 shadow-xs overflow-hidden">

          {isSubmitting && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-xs z-20 flex items-center justify-center gap-2 text-xs font-bold text-slate-700">
              <RefreshCw
                size={18}
                className="animate-spin text-[#6DBE45]"
              />

              Updating revision response...
            </div>
          )}

          <div className="flex items-start justify-between gap-4">

            <div className="flex items-start gap-3 flex-1 min-w-0">

              <div className="p-2 rounded-xl bg-amber-100 text-amber-700 shrink-0">
                <Clock size={16} />
              </div>

              <div className="flex-1 min-w-0">

                <p className="text-sm font-bold text-amber-900">
                  Action Required: Order Revision Pending
                </p>

                <p className="text-xs text-slate-600 mt-0.5">
                  {revision.remarks ||
                    "Some ordered items were out of stock or updated by store management."}
                </p>

              </div>
            </div>

            <div className="flex flex-col items-end shrink-0">

              <span className="text-[10px] uppercase font-bold text-slate-400">
                Timer
              </span>

              <p className="text-xl font-black text-red-600 leading-none mt-0.5">
                {formatCountdown(
                  remainingSeconds,
                )}
              </p>

            </div>
          </div>

          <div className="space-y-3 pt-1">

            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-900">
              Review Changes
            </p>

            {revision.changes?.map(
              (change: any, changeIdx: number) => {
                const original =
                  change.originalItem;

                const replacements =
                  change.replacementItems || [];

                const decision =
                  decisions[changeIdx];

                const isReplacement =
                  change.changeType ===
                  "Replacement";

                const orderedQty =
                  original?.orderedQuantity ?? 1;

                const remainingQty =
                  original?.remainingQuantity ?? 0;

                const replacementQty =
                  original?.replacementQuantity ?? 0;

                const removedQty =
                  original?.removedQuantity ?? 0;

                return (
                  <div
                    key={
                      change.revisionItemId ||
                      changeIdx
                    }
                    className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3 shadow-xs"
                  >

                    <div className="flex items-center justify-between gap-3">

                      <div className="flex items-center gap-2 flex-wrap">

                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border ${isReplacement
                            ? toneClasses.replace
                            : toneClasses.warning
                            }`}
                        >
                          {isReplacement ? (
                            <Repeat size={13} />
                          ) : (
                            <XCircle size={13} />
                          )}

                          {isReplacement
                            ? "Replacement Offered"
                            : "Quantity Adjusted"}
                        </span>

                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-extrabold bg-slate-100 text-slate-700 border border-slate-200">
                          Ordered: {orderedQty}

                          {remainingQty > 0 &&
                            ` | ${remainingQty} Kept`}

                          {replacementQty > 0 &&
                            ` | ${replacementQty} Replaced`}

                          {removedQty > 0 &&
                            !replacementQty &&
                            ` | ${removedQty} Removed`}
                        </span>

                      </div>
                    </div>

                    {change.reason && (
                      <p className="text-xs text-slate-500 italic flex items-center gap-1">
                        <Info
                          size={12}
                          className="text-slate-400 shrink-0"
                        />

                        Reason: "{change.reason}"
                      </p>
                    )}

                    {isReplacement &&
                      replacements.length > 0 && (
                        <div className="bg-blue-50/40 rounded-xl border border-blue-100 p-3 space-y-2">

                          <p className="text-[10px] font-bold uppercase tracking-wide text-blue-600 flex items-center gap-1">
                            <Repeat size={11} />
                            Replaced With:
                          </p>

                          {replacements.map(
                            (rep: any) => (
                              <div
                                key={
                                  rep.productId ||
                                  rep.orderItemId
                                }
                                className="flex items-center gap-3 bg-white rounded-lg border border-blue-100 p-2.5 shadow-2xs"
                              >

                                {rep.imageUrl ? (
                                  <img
                                    src={rep.imageUrl}
                                    alt={rep.name}
                                    className="w-11 h-11 object-cover rounded-lg border border-slate-100 shrink-0"
                                  />
                                ) : (
                                  <div className="w-11 h-11 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-[9px] font-medium shrink-0">
                                    No Img
                                  </div>
                                )}

                                <div className="flex-1 min-w-0">

                                  <p className="text-xs font-bold text-slate-900 truncate">
                                    {rep.name}
                                  </p>

                                  {rep.subtitle && (
                                    <p className="text-[10px] text-slate-400">
                                      {rep.subtitle}
                                    </p>
                                  )}

                                  <p className="text-[10px] text-slate-500 font-medium">
                                    Qty: {rep.quantity}
                                  </p>

                                </div>

                                <p className="text-xs font-black text-blue-600 whitespace-nowrap">
                                  {currency}{" "}
                                  {rep.unitSalePrice.toFixed(
                                    2,
                                  )}
                                </p>

                              </div>
                            ),
                          )}

                        </div>
                      )}

                    <div className="flex items-start justify-between gap-3 pt-2 border-t border-dashed border-rose-200">

                      <div className="flex items-start gap-3 flex-1 min-w-0">

                        {original?.imageUrl ? (
                          <img
                            src={original.imageUrl}
                            alt={original.name}
                            className="w-12 h-12 object-cover rounded-lg border border-slate-100 shrink-0 opacity-70"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-[9px] font-medium shrink-0">
                            No Img
                          </div>
                        )}

                        <div className="flex-1 min-w-0">

                          <p className="text-xs font-bold line-through decoration-rose-500 text-rose-500">
                            {original?.name}
                          </p>

                          {original?.subtitle && (
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              {original.subtitle}
                            </p>
                          )}

                          <div className="flex items-center gap-2 mt-1 flex-wrap">

                            <p className="text-[10px] text-slate-500 font-semibold">
                              {currency}{" "}
                              {(
                                original?.unitSalePrice ??
                                0
                              ).toFixed(2)}{" "}
                              • Qty {orderedQty}
                            </p>

                            <span className="text-[9px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200 uppercase">
                              {change.targetType ||
                                "PRODUCT"}
                            </span>

                          </div>
                        </div>
                      </div>

                      <p className="text-xs font-black text-rose-500 line-through decoration-rose-500 whitespace-nowrap">
                        {currency}{" "}
                        {(
                          (original?.unitSalePrice ??
                            0) *
                          orderedQty
                        ).toFixed(2)}
                      </p>

                    </div>

                    {(revision.canAccept ||
                      revision.canReject) && (
                        <div className="flex-1 flex justify-center">

                          <div className="flex items-center gap-1.5 shrink-0">

                            <button
                              type="button"
                              disabled={isSubmitting}
                              onClick={() =>
                                handleDecision(
                                  changeIdx,
                                  "Accept",
                                )
                              }
                              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${decision === "Accept"
                                ? "bg-green-600 text-white border-green-600 shadow-sm"
                                : "bg-green-100 text-green-700 border-green-300 hover:bg-green-200 hover:border-green-400"
                                }`}
                            >
                              <CheckCircle2 size={16} />
                              Accept
                            </button>

                            <button
                              type="button"
                              disabled={isSubmitting}
                              onClick={() =>
                                handleDecision(
                                  changeIdx,
                                  "Reject",
                                )
                              }
                              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${decision === "Reject"
                                ? "bg-red-600 text-white border-red-600 shadow-sm"
                                : "bg-red-100 text-red-700 border-red-300 hover:bg-red-200 hover:border-red-400"
                                }`}
                            >
                              <XCircle size={16} />
                              Reject
                            </button>

                          </div>
                        </div>
                      )}

                  </div>
                );
              },
            )}
          </div>

          <div className="bg-white rounded-xl border border-amber-100 p-3 grid grid-cols-2 gap-3 text-xs">

            <div>

              <p className="text-slate-400 font-semibold">
                Original Total
              </p>

              <p className="font-bold text-slate-500 line-through">
                {currency}{" "}
                {revision.originalTotal.toFixed(2)}
              </p>

              <p className="text-slate-400 font-semibold mt-1">
                Revised Total
              </p>

              <p className="font-black text-[#2F5E2E] text-base">
                {currency}{" "}
                {revision.revisedTotal.toFixed(2)}
              </p>

            </div>

            <div className="text-right">

              <p className="text-slate-400 font-semibold">
                {revision.refundAmount > 0
                  ? "Refund Balance"
                  : "Extra Payment Required"}
              </p>

              <p
                className={`font-black text-base ${revision.refundAmount > 0
                  ? "text-emerald-600"
                  : "text-rose-600"
                  }`}
              >
                {currency}{" "}
                {(
                  revision.refundAmount > 0
                    ? revision.refundAmount
                    : revision.additionalPayment
                ).toFixed(2)}
              </p>

            </div>
          </div>

        </div>
      )}

      {/* Pending Settlement Banner */}
      {!hasPendingRevision &&
        (isPaymentPending || isRefundPending) && (
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">

            {isPaymentPending && (
              <>
                <h3 className="text-sm font-bold text-blue-800">
                  Revision Accepted – Additional Payment Required
                </h3>

                <p className="text-xs text-blue-700 mt-2">
                  You accepted the revised order. Please pay the
                  remaining amount to continue processing your order.
                </p>

                <div className="mt-4 rounded-xl bg-white border p-4">

                  <div className="flex justify-between">

                    <span>
                      Additional Payment
                    </span>

                    <span className="font-bold text-red-600">
                      {currency}{" "}
                      {revision.additionalPayment.toFixed(2)}
                    </span>

                  </div>
                </div>

                <button
                  onClick={() =>
                    handleRetrypayment(
                      revision.revisionId,
                    )
                  }
                  className="mt-4 w-full bg-[#6DBE45] text-white rounded-xl py-3 font-semibold hover:bg-[#5fb63f] transition-colors"
                >
                  Pay {currency}{" "}
                  {revision.additionalPayment.toFixed(2)}
                </button>
              </>
            )}

            {isRefundPending && (
              <>
                <h3 className="text-sm font-bold text-green-800">
                  Revision Accepted – Waiting For Refund
                </h3>

                <p className="text-xs text-green-700 mt-2">
                  Your refund is currently being processed. The amount
                  will be credited to your wallet or original payment
                  method once completed.
                </p>

                <div className="mt-4 rounded-xl bg-white border p-4">

                  <div className="flex justify-between">

                    <span>
                      Refund Amount
                    </span>

                    <span className="font-bold text-green-600">
                      {currency}{" "}
                      {revision.refundAmount.toFixed(2)}
                    </span>

                  </div>
                </div>
              </>
            )}

          </div>
        )}

      {/* Order Items Section */}
      <div className="space-y-4">

        <div className="flex items-center justify-between">

          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
            Order Items
          </p>

        </div>

        {(data.items || []).map(
          (item: any) => {
            const isAlreadyReviewed =
              item.reviewed === true ||
              (item.rating !== null &&
                item.rating !== undefined &&
                item.rating > 0);

            /*
             * ZERO QUANTITY ITEM
             */
            if (item.quantity === 0) {
              const originalQty = 1;

              const itemPrice =
                item.unitPrice > 0
                  ? item.unitPrice
                  : item.unitSalePrice;

              return (
                <div
                  key={`${item.itemType}-${item.orderLineId}`}
                  className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-xs"
                >

                  <div className="flex items-start justify-between gap-3 pt-1 border-t border-dashed border-rose-200">

                    <div className="flex items-start gap-3 flex-1 min-w-0">

                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="w-14 h-14 object-cover rounded-lg border border-slate-100 shrink-0 opacity-70"
                        />
                      ) : (
                        <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-[10px] font-medium shrink-0">
                          No Image
                        </div>
                      )}

                      <div className="flex-1 min-w-0">

                        <h4 className="font-bold text-sm text-rose-500 line-through decoration-rose-500 leading-snug">
                          {item.name}
                        </h4>

                        {item.subtitle && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            {item.subtitle}
                          </p>
                        )}

                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">

                          <p className="text-xs font-semibold text-slate-500">
                            {currency}{" "}
                            {itemPrice.toFixed(2)}{" "}
                            • Qty {originalQty}
                          </p>

                          <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200 uppercase">
                            {item.itemType ||
                              "PRODUCT"}
                          </span>

                        </div>
                      </div>
                    </div>

                    <p className="text-sm font-black text-rose-500 line-through decoration-rose-500 whitespace-nowrap">
                      {currency}{" "}
                      {itemPrice.toFixed(2)}
                    </p>

                  </div>
                </div>
              );
            }

            const currentItemState =
              bulkReviews[item.orderLineId] || {
                rating: item.rating || 0,
                comment:
                  item.reviewComment || "",
                files: [],
                previews: [],
              };

            return (
              <div
                key={`${item.itemType}-${item.orderLineId}`}
                className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3 shadow-xs"
              >

                {/* ITEM INFORMATION */}
                <div className="flex items-start justify-between gap-3">

                  <div className="flex items-start gap-3 flex-1 min-w-0">

                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="w-14 h-14 object-cover rounded-lg border border-slate-100 shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-[10px] font-medium shrink-0">
                        No Image
                      </div>
                    )}

                    <div className="flex-1 min-w-0">

                      <p className="font-bold text-slate-900 text-sm truncate">
                        {item.name}
                      </p>

                      {item.subtitle && (
                        <p className="text-xs text-slate-400">
                          {item.subtitle}
                        </p>
                      )}

                      {/* SUBSCRIPTION */}
                      {item.isSubscription && (
                        <span className="inline-block mt-1 text-[10px] text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded font-bold">
                          Sub:{" "}
                          {item.billingInterval ||
                            "Recurring"}{" "}
                          (
                          {item.billingCycle ||
                            "Monthly"}
                          )
                        </span>
                      )}

                      {/* BUNDLE */}
                      {item.isBundleItem ? (
                        <div className="mt-1 space-y-1.5">

                          {/* Bundle pricing */}
                          <div className="flex items-center gap-2 flex-wrap">

                            <span className="text-[11px] text-slate-400 line-through">
                              {currency}{" "}
                              {Number(
                                item.originalPrice ??
                                item.unitPrice ??
                                0,
                              ).toFixed(2)}
                            </span>

                            <span className="text-xs font-bold text-[#5FB63F]">
                              {currency}{" "}
                              {Number(
                                item.bundleUnitPrice ??
                                item.unitSalePrice ??
                                item.totalPrice ??
                                0,
                              ).toFixed(2)}
                            </span>

                            <p className="text-xs font-semibold text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">

                              {currency}{" "}
                              {Number(
                                item.unitSalePrice ??
                                0,
                              ).toFixed(2)}{" "}
                              • Qty {item.quantity}

                              {/* PET PURCHASE DETAILS */}
                              {item.itemType?.toLowerCase() ===
                                "pet" && (
                                  <>
                                    {item.purchaseType && (
                                      <span className="text-[10px] font-bold bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200 uppercase">
                                        {item.purchaseType ===
                                          "PartialBooking"
                                          ? "Partial Booking"
                                          : item.purchaseType ===
                                            "FullPayment"
                                            ? "Full Payment"
                                            : item.purchaseType}
                                      </span>
                                    )}

                                    {item.purchaseAmount !==
                                      undefined &&
                                      item.purchaseAmount !==
                                      null && (
                                        <span className="text-[10px] font-bold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200">
                                          Paid:{" "}
                                          {currency}{" "}
                                          {Number(
                                            item.purchaseAmount,
                                          ).toFixed(2)}
                                        </span>
                                      )}

                                    {item.remainingAmount !==
                                      undefined &&
                                      item.remainingAmount !==
                                      null && (
                                        <span className="text-[10px] font-bold bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">
                                          Remaining:{" "}
                                          {currency}{" "}
                                          {Number(
                                            item.remainingAmount,
                                          ).toFixed(2)}
                                        </span>
                                      )}
                                  </>
                                )}

                            </p>
                          </div>

                          {/* Bundle information */}
                          <div className="flex items-center gap-2 flex-wrap">

                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-1 rounded-md">
                              🎁{" "}
                              {item.bundleName ||
                                "Bundle"}
                            </span>

                            {Number(
                              item.savingAmount ?? 0,
                            ) > 0 && (
                                <span className="text-[10px] font-semibold text-emerald-600">
                                  You save{" "}
                                  {currency}{" "}
                                  {Number(
                                    item.savingAmount,
                                  ).toFixed(2)}
                                </span>
                              )}

                          </div>
                        </div>
                      ) : (
                        /*
                         * NORMAL ITEM
                         *
                         * THIS IS THE IMPORTANT FIX.
                         *
                         * Your API has:
                         * isBundleItem: false
                         *
                         * So the old pet details inside the
                         * bundle branch were never rendered.
                         */
                        <div className="mt-1">

                          <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 flex-wrap">

                            {currency}{" "}
                            {Number(
                              item.unitSalePrice ??
                              0,
                            ).toFixed(2)}{" "}
                            • Qty {item.quantity}

                            {/* PET PURCHASE DETAILS */}
                            {item.itemType?.toLowerCase() ===
                              "pet" && (
                                <>
                                  {/* PURCHASE TYPE */}
                                  {item.purchaseType && (
                                    <span className="text-[10px] font-bold bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200 uppercase">
                                      {item.purchaseType ===
                                        "PartialBooking"
                                        ? "Partial Booking"
                                        : item.purchaseType ===
                                          "FullPayment"
                                          ? "Full Payment"
                                          : item.purchaseType}
                                    </span>
                                  )}

                                  {/* PAID AMOUNT */}
                                  {item.purchaseAmount !==
                                    undefined &&
                                    item.purchaseAmount !==
                                    null && (
                                      <span className="text-[10px] font-bold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200">
                                        Paid:{" "}
                                        {currency}{" "}
                                        {Number(
                                          item.purchaseAmount,
                                        ).toFixed(2)}
                                      </span>
                                    )}

                                  {/* REMAINING AMOUNT */}
                                  {item.remainingAmount !==
                                    undefined &&
                                    item.remainingAmount !==
                                    null && (
                                      <span className="text-[10px] font-bold bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">
                                        Remaining:{" "}
                                        {currency}{" "}
                                        {Number(
                                          item.remainingAmount,
                                        ).toFixed(2)}
                                      </span>
                                    )}
                                </>
                              )}

                          </p>

                        </div>
                      )}

                    </div>
                  </div>

                  <p className="text-sm font-black text-[#5FB63F] whitespace-nowrap">
                    {currency}{" "}
                    {Number(
                      item.totalPrice ?? 0,
                    ).toFixed(2)}
                  </p>

                </div>

                {/* SERVICE BOOKING */}
                {item.itemType?.toLowerCase() ===
                  "service" && (
                    <button
                      type="button"
                      onClick={() => {
                        navigate(
                          "/customerprofilescreen",
                          {
                            state: {
                              activeTab:
                                "myBookings",
                              search:
                                data.header
                                  .orderNumber,
                            },
                          },
                        );
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-orange-50 border border-orange-200 text-mainBrandColor text-xs font-bold hover:bg-orange-100 transition-colors"
                    >
                      <Calendar size={13} />
                      View Bookings
                    </button>
                  )}

                {/* REVIEW SECTION */}
                {isAlreadyReviewed ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1.5">

                    <div className="flex items-center justify-between">

                      <span className="text-[11px] font-bold text-slate-600">
                        Your Rating
                      </span>

                      <div className="flex items-center gap-1">

                        {[1, 2, 3, 4, 5].map(
                          (star) => (
                            <Star
                              key={star}
                              size={14}
                              className={
                                star <=
                                  (item.rating || 0)
                                  ? "fill-amber-400 text-amber-400"
                                  : "text-slate-300"
                              }
                            />
                          ),
                        )}

                      </div>
                    </div>

                    {item.reviewComment && (
                      <p className="text-xs text-slate-600 italic bg-white p-2 rounded-lg border border-slate-100">
                        "{item.reviewComment}"
                      </p>
                    )}

                    {item.reviewImages &&
                      item.reviewImages.length >
                      0 && (
                        <div className="flex items-center gap-2 pt-2 flex-wrap">

                          {item.reviewImages.map(
                            (
                              img: string,
                              imgIdx: number,
                            ) => (
                              <img
                                key={imgIdx}
                                src={img}
                                alt="Review attachment"
                                className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                              />
                            ),
                          )}

                        </div>
                      )}

                  </div>
                ) : isDelivered ? (
                  <div className="bg-emerald-50/40 border border-emerald-100 rounded-xl p-3 space-y-3">

                    <div className="flex items-center justify-between flex-wrap gap-2">

                      <span className="text-xs font-bold text-slate-700">
                        Rate this product:
                      </span>

                      <div className="flex items-center gap-1">

                        {[1, 2, 3, 4, 5].map(
                          (star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() =>
                                handleRatingChange(
                                  item.orderLineId,
                                  star,
                                )
                              }
                              className="p-1 hover:scale-110 transition-transform"
                            >
                              <Star
                                size={18}
                                className={
                                  star <=
                                    currentItemState.rating
                                    ? "fill-amber-400 text-amber-400"
                                    : "text-slate-300 hover:text-amber-300"
                                }
                              />
                            </button>
                          ),
                        )}

                      </div>
                    </div>

                    <input
                      type="text"
                      value={
                        currentItemState.comment
                      }
                      onChange={(e) =>
                        handleCommentChange(
                          item.orderLineId,
                          e.target.value,
                        )
                      }
                      placeholder="Write a brief comment (optional)..."
                      className="w-full text-xs bg-white border border-slate-200 rounded-lg p-2.5 outline-none focus:border-[#6DBE45] transition-colors"
                    />

                    {/* Image Upload Area */}
                    <div className="space-y-2">

                      <div className="flex items-center gap-2 flex-wrap">

                        {currentItemState.previews.map(
                          (
                            preview,
                            previewIdx,
                          ) => (
                            <div
                              key={previewIdx}
                              className="relative w-14 h-14 rounded-lg overflow-hidden border border-slate-200 shrink-0"
                            >

                              <img
                                src={preview}
                                alt="preview"
                                className="w-full h-full object-cover"
                              />

                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveImage(
                                    item.orderLineId,
                                    previewIdx,
                                  )
                                }
                                className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 text-white rounded-full hover:bg-black"
                              >
                                <X size={10} />
                              </button>

                            </div>
                          ),
                        )}

                        <label className="w-14 h-14 rounded-lg border-2 border-dashed border-emerald-300 hover:border-emerald-500 bg-white flex flex-col items-center justify-center text-emerald-600 cursor-pointer transition-colors shrink-0">

                          <ImagePlus size={16} />

                          <span className="text-[9px] font-bold mt-0.5">
                            Photo
                          </span>

                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) =>
                              handleAddImages(
                                item.orderLineId,
                                e,
                              )
                            }
                          />

                        </label>

                      </div>
                    </div>

                  </div>
                ) : null}

              </div>
            );
          },
        )}

        {/* Bottom Save Reviews Button */}
        {hasUnreviewedDeliveredItems && (
          <div className="flex justify-end pt-2">

            <button
              onClick={handleSubmitAllReviews}
              disabled={isSubmittingReviews}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#2F5E2E] hover:bg-[#6DBE45] text-white rounded-xl text-sm font-bold transition-all shadow-md active:scale-95 disabled:opacity-50"
            >

              {isSubmittingReviews ? (
                <RefreshCw
                  size={16}
                  className="animate-spin"
                />
              ) : (
                <Send size={16} />
              )}

              Submit All Reviews

            </button>

          </div>
        )}

      </div>

      {/* Delivery Address & Pricing Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Address Card */}
        <div className="bg-slate-50 rounded-2xl border border-slate-100 p-4 space-y-2">

          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <MapPin size={12} />
            Delivery Address
          </p>

          <p className="text-xs text-slate-800 font-bold">
            {data.address?.fullName}
          </p>

          <p className="text-xs text-slate-500 leading-relaxed">
            {[
              data.address?.addressLine1,
              data.address?.addressLine2,
              data.address?.city,
              data.address?.state,
            ]
              .filter(Boolean)
              .join(", ")}
          </p>

          <p className="text-xs text-slate-400">
            {data.address?.country} -{" "}
            {data.address?.postalCode}
          </p>

          {data.address?.phoneNumber && (
            <p className="text-xs font-semibold text-slate-600 mt-1">
              Ph: {data.address.phoneNumber}
            </p>
          )}

        </div>

        {/* Pricing Card */}
        <div className="bg-slate-50 rounded-2xl border border-slate-100 p-4 space-y-3">

          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Receipt size={13} />
            Pricing Breakdown
          </p>

          <div className="space-y-2 text-xs font-medium text-slate-600">

            <div className="flex justify-between">

              <span>Subtotal</span>

              <span className="font-semibold text-slate-900">
                {currency}{" "}
                {(
                  p?.subTotal ??
                  p?.productTotal ??
                  0
                ).toFixed(2)}
              </span>

            </div>

            {remainingAmount > 0 && (
              <div className="flex justify-between">
                <span>Remaining Amount</span>

                <span className="font-semibold text-slate-900">
                  {currency} {remainingAmount.toFixed(2)}
                </span>
              </div>
            )}


            {totalSaving > 0 && (
              <div className="flex justify-between text-green-600">

                <span className="flex items-center gap-1">
                  <Tag size={11} />
                  Total Savings
                </span>

                <span className="font-bold">
                  -{currency}{" "}
                  {totalSaving.toFixed(2)}
                </span>

              </div>
            )}

            {rewardPointsEarned > 0 && (
              <div className="flex justify-between text-green-600">

                <span className="flex items-center gap-1">
                  <GiftIcon size={11} />
                  Earned Petland Points
                </span>

                <span className="font-bold">
                  {rewardPointsEarned.toFixed(2)}
                </span>

              </div>
            )}

            {couponDisc > 0 && (
              <div className="flex justify-between text-green-600">

                <span className="flex items-center gap-1">
                  <Tag size={11} />

                  Coupon
                  {p?.couponCode
                    ? ` (${p.couponCode})`
                    : ""}
                </span>

                <span className="font-bold">
                  -{currency}{" "}
                  {couponDisc.toFixed(2)}
                </span>

              </div>
            )}

            {rewardDisc > 0 && (
              <div className="flex justify-between text-green-600">

                <span className="flex items-center gap-1">
                  <Gift size={11} />

                  Used PetLand Points (
                  {rewardPointsUsed})
                </span>

                <span className="font-bold">
                  -{currency}{" "}
                  {rewardDisc.toFixed(2)}
                </span>

              </div>
            )}

            {vatAmount > 0 && (
              <div className="flex justify-between text-slate-500">

                <span>VAT</span>

                <span className="font-semibold text-slate-700">
                  {currency}{" "}
                  {vatAmount.toFixed(2)}
                </span>

              </div>
            )}

            {walletUsed > 0 && (
              <div className="flex justify-between text-green-600">

                <span className="flex items-center gap-1">
                  <Wallet size={12} />
                  Wallet
                </span>

                <span className="font-bold">
                  -{currency}{" "}
                  {walletUsed.toFixed(2)}
                </span>

              </div>
            )}

            <div className="flex justify-between">

              <span>Delivery Fee</span>

              <span className="font-semibold text-slate-900">
                {deliveryFee > 0
                  ? `${currency} ${deliveryFee.toFixed(
                    2,
                  )}`
                  : "FREE"}
              </span>

            </div>

            {platformFee > 0 && (
              <div className="flex justify-between">

                <span>Platform Fee</span>

                <span className="font-semibold text-slate-900">
                  {currency}{" "}
                  {platformFee.toFixed(2)}
                </span>

              </div>
            )}

            {smallOrderFee > 0 && (
              <div className="flex justify-between">

                <span>Small Order Fee</span>

                <span className="font-semibold text-slate-900">
                  {currency}{" "}
                  {smallOrderFee.toFixed(2)}
                </span>

              </div>
            )}

            <div className="border-t border-slate-200/80 pt-2 flex justify-between items-center font-bold text-slate-900">

              <span>Order Total</span>

              <span className="text-base text-[#2F5E2E] font-black">
                {currency}{" "}
                {totalAmount.toFixed(2)}
              </span>

            </div>

            {additionalPaymentAmount > 0 && (
              <div className="flex justify-between items-center text-rose-600 font-semibold pt-1 border-t border-dashed border-slate-200">

                <span className="flex items-center gap-1">
                  <PlusCircle size={12} />
                  Additional Payment Paid
                </span>

                <span className="font-bold">
                  +{currency}{" "}
                  {additionalPaymentAmount.toFixed(
                    2,
                  )}
                </span>

              </div>
            )}

            {refundedAmount > 0 && (
              <div className="flex justify-between items-center text-emerald-600 font-semibold pt-1 border-t border-dashed border-slate-200">

                <span className="flex items-center gap-1">
                  <RotateCcw size={12} />
                  Amount Refunded
                </span>

                <span className="font-bold">
                  -{currency}{" "}
                  {refundedAmount.toFixed(2)}
                </span>

              </div>
            )}

          </div>

          <div className="pt-2 border-t border-slate-200/80 space-y-1.5 text-[11px]">

            {gatewayAmount > 0 && (
              <div className="flex justify-between items-center text-blue-900 bg-blue-50 px-2.5 py-2 rounded-lg border border-blue-200 font-extrabold text-xs shadow-2xs">

                <span className="flex items-center gap-1.5">
                  <CreditCard
                    size={13}
                    className="text-blue-600"
                  />

                  Revised Total
                </span>

                <span className="text-blue-700">
                  {currency}{" "}
                  {gatewayAmount.toFixed(2)}
                </span>

              </div>
            )}

          </div>

        </div>
      </div>
    </div>
  );
};

export default CustomerOrderDetails;